/* global Kakao, naver */

// ==========================================
// 🔑 API KEY 설정 (여기에 키를 입력하세요)
// ==========================================
const KAKAO_JAVASCRIPT_KEY = process.env.REACT_APP_KAKAO_JAVASCRIPT_KEY || "710e35d89b99d8a2624ce4e6656818d7";
const NAVER_CLIENT_ID = process.env.REACT_APP_NAVER_CLIENT_ID || "iOL5zES0OKrfAf2LSlzc";
// ==========================================

export const initializeKakao = () => {
    // Legacy SDK V1 Init
    if (window.Kakao) {
        if (!window.Kakao.isInitialized()) {
            try {
                window.Kakao.init(KAKAO_JAVASCRIPT_KEY);
                console.log("Kakao Legacy SDK Initialized");
            } catch (e) {
                console.error("Kakao Init Failed:", e);
            }
        }
    }
};

export const loginWithKakao = () => {
    return new Promise((resolve, reject) => {
        if (!window.Kakao) {
            alert("카카오 SDK가 로드되지 않았습니다.");
            return reject(new Error("Kakao SDK not loaded"));
        }

        if (!window.Kakao.isInitialized()) {
            window.Kakao.init(KAKAO_JAVASCRIPT_KEY);
        }

        // Legacy V1 Login
        window.Kakao.Auth.login({
            success: (authObj) => {
                window.Kakao.API.request({
                    url: '/v2/user/me',
                    success: (res) => {
                        const kakaoAccount = res.kakao_account;
                        const userProfile = {
                            id: res.id,
                            email: kakaoAccount.email || `kakao_${res.id}@no-email.com`,
                            name: kakaoAccount.profile?.nickname || "카카오 사용자",
                            provider: "kakao"
                        };
                        resolve(userProfile);
                    },
                    fail: (err) => reject(err)
                });
            },
            fail: (err) => {
                console.error("Kakao Login Failed", err);
                alert("카카오 로그인 실패: " + JSON.stringify(err));
                reject(err);
            }
        });
    });
};

export const initializeNaver = () => {
    // Naver SDK init on demand
};

export const loginWithNaver = () => {
    return new Promise((resolve, reject) => {
        try {
            const redirectUri = encodeURIComponent(window.location.origin + "/naver/callback");

            // [Fix] Generate and store state for CSRF protection (Naver Guide Recommendation)
            const state = Math.random().toString(36).substring(7);
            localStorage.setItem("naver_login_state", state);

            const authUrl = `https://nid.naver.com/oauth2.0/authorize?response_type=token&client_id=${NAVER_CLIENT_ID}&redirect_uri=${redirectUri}&state=${state}`;

            console.log("Opening Naver Login Popup (State Saved):", state);
            const popup = window.open(authUrl, "naver_login", "width=600,height=800");

            if (!popup) {
                alert("반드시 팝업 차단을 해제해야 로그인이 가능합니다.");
                reject(new Error("Popup blocked"));
            } else {
                resolve({ action: "naver_popup_opened", popup });
            }
        } catch (e) {
            console.error("Naver Login Error:", e);
            reject(e);
        }
    });
};

export const handleNaverCallback = () => {
    return new Promise((resolve) => {
        console.log("[NaverCallback] Starting state-aware hybrid callback process...");

        // [STEP 1] URL에서 직접 토큰 및 상태값 추출
        const hash = window.location.hash;
        let accessToken = null;
        let returnedState = null;

        if (hash) {
            const params = new URLSearchParams(hash.substring(1));
            accessToken = params.get('access_token');
            returnedState = params.get('state');
            console.log("[NaverCallback] Extracted AccessToken:", accessToken ? "YES" : "NO", "State:", returnedState);
        }

        // [STEP 2] 상태값(State) 검증
        const savedState = localStorage.getItem("naver_login_state");
        localStorage.removeItem("naver_login_state"); // 일회용 사용 후 즉시 삭제

        const isStateValid = returnedState && returnedState === savedState;
        console.log("[NaverCallback] State validation result:", isStateValid ? "SUCCESS" : "FAILED (Potential CSRF or session lost)");

        if (!window.naver) {
            console.error("Naver SDK not found");
            return resolve(null);
        }

        try {
            const naverLogin = new window.naver.LoginWithNaverId({
                clientId: NAVER_CLIENT_ID,
                callbackUrl: window.location.origin + "/naver/callback",
                isPopup: true,
                callbackHandle: true
            });

            naverLogin.init();

            const timeout = setTimeout(() => {
                console.warn("Naver getLoginStatus Timeout - Attempting manual fallback if token exists");
                if (accessToken && isStateValid) {
                    manualProfileFetch(accessToken, resolve);
                } else {
                    failureCallback('TIMEOUT', resolve);
                }
            }, 6000);

            naverLogin.getLoginStatus(async (status) => {
                clearTimeout(timeout);
                console.log("Naver Login Status Callback - Status:", status);

                if (status && naverLogin.user && isStateValid) {
                    const userProfile = {
                        id: naverLogin.user.id,
                        email: naverLogin.user.email,
                        name: naverLogin.user.name || "네이버 사용자",
                        provider: "naver"
                    };
                    finishCallback(userProfile, resolve);
                } else if (accessToken && isStateValid) {
                    console.log("[NaverCallback] SDK status failed or user missing, but token/state valid. Manually fetching...");
                    manualProfileFetch(accessToken, resolve);
                } else {
                    const errorMsg = !isStateValid ? "CSRF_ERROR" : "STATUS_FAILED";
                    failureCallback(errorMsg, resolve);
                }
            });
        } catch (e) {
            console.error("HandleNaverCallback Error:", e);
            failureCallback(e.message, resolve);
        }
    });
};

const manualProfileFetch = (accessToken, resolve) => {
    try {
        const script = document.createElement('script');
        const callbackName = 'naverProfileCallback_' + Date.now();

        window[callbackName] = (res) => {
            console.log("[NaverCallback] Manual JSONP response:", res);
            if (res.resultcode === '00') {
                const userProfile = {
                    id: res.response.id,
                    email: res.response.email,
                    name: res.response.name || "네이버 사용자",
                    provider: "naver"
                };
                finishCallback(userProfile, resolve);
            } else {
                failureCallback("Manual Profile Fetch Failed", resolve);
            }
            delete window[callbackName];
            script.remove();
        };

        script.src = `https://openapi.naver.com/v1/nid/me?access_token=${accessToken}&callback=${callbackName}`;
        document.body.appendChild(script);
    } catch (e) {
        console.error("Manual fetch error", e);
        failureCallback("MANUAL_FETCH_ERROR", resolve);
    }
};

const finishCallback = (userProfile, resolve) => {
    const targetOrigin = window.location.origin;
    if (window.opener) {
        console.log("Success: Sending profile to opener and closing popup...");
        window.opener.postMessage({ type: 'NAVER_LOGIN_SUCCESS', userProfile }, targetOrigin);
    }

    // Always close current popup window
    setTimeout(() => {
        window.close();
        resolve(userProfile);
    }, 500);
};

const failureCallback = (error, resolve) => {
    const targetOrigin = window.location.origin;
    console.warn("Naver Login process failed:", error);
    if (window.opener) {
        window.opener.postMessage({ type: 'NAVER_LOGIN_FAILURE', error }, targetOrigin);
    }

    // Always close current popup window
    setTimeout(() => {
        window.close();
        resolve(null);
    }, 1000);
};
